// Loading skeletons for the /client portal, drawn to the same layout as the
// real pages (Luxe editorial design) so nothing jumps when content lands.
//
//   <HomeSkeleton />          masthead + member card + up-next strip
//   <DashboardSkeleton />     member salon cover + stats + contents
//   <EditProfileSkeleton />   sidebar / tab strip + underline form
//   <ChatSkeleton />          bubbles + composer (inside the chat page)
//   <ClientRouteSkeleton />   whole-screen skeleton for a /client URL, with the
//                             portal chrome (desktop masthead / mobile tab bar);
//                             used while the auth check runs and while a page's
//                             code chunk loads.
import React from 'react';
import { L, LuxeStyles, DiamondMark, useIsDesktop } from '../../theme/luxe';

const DESKTOP = 900;

// One shimmering block. `lx-skel` (theme/luxe.js) supplies the sheen.
export const Sk = ({ w = '100%', h = 12, r = L.radius, style }) => (
  <div className="lx-skel" style={{ width: w, height: h, borderRadius: r, flexShrink: 0, ...style }} />
);
const Rule = ({ style }) => <div style={{ height: 1, background: L.line, ...style }} />;
const Circle = ({ d = 40, style }) => <Sk w={d} h={d} r="50%" style={style} />;

// ── Home ──────────────────────────────────────────────────────────────────
// The member card + up-next strip (also used inside ClientHome while the
// first page of members loads, under the real masthead).
export const HomeCardSkeleton = () => {
  const isDesktop = useIsDesktop(DESKTOP);
  if (isDesktop) {
    return (
      <>
        {/* stage: arrows + two-page card */}
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '26px 40px 0', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 28 }}>
          <Circle d={46} />
          <div style={{
            flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '1.08fr 1fr', padding: 18, boxSizing: 'border-box',
            height: 'clamp(540px, calc(100vh - 76px - 300px), 740px)', background: L.card, boxShadow: L.shadowLg, borderRadius: L.radiusLg,
          }}>
            <Sk h="100%" r={0} />
            <div style={{ padding: '30px 34px 24px 46px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <Sk w={28} h={1} /><Sk w={70} h={10} /><span style={{ flex: 1 }} /><Sk w={56} h={18} />
              </div>
              <Sk w="72%" h={58} style={{ marginTop: 28 }} />
              <Sk w="38%" h={20} style={{ marginTop: 14 }} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 30, borderTop: `1px solid ${L.line}` }}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={{ padding: '14px 12px 14px 0', borderBottom: `1px solid ${L.line}` }}>
                    <Sk w="45%" h={9} /><Sk w="70%" h={18} style={{ marginTop: 8 }} />
                  </div>
                ))}
              </div>
              <Sk w="92%" h={16} style={{ marginTop: 26 }} />
              <Sk w="84%" h={16} style={{ marginTop: 10 }} />
              <Sk w="60%" h={16} style={{ marginTop: 10 }} />
              <span style={{ flex: 1 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}><Circle d={44} /><Sk w={110} h={11} /></div>
            </div>
          </div>
          <Circle d={46} />
        </div>
        {/* up-next strip */}
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '30px 40px 0', boxSizing: 'border-box', display: 'flex', alignItems: 'flex-end', gap: 22 }}>
          <Sk w={64} h={10} style={{ marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 12, flex: 1, overflow: 'hidden', padding: 6 }}>
            {Array.from({ length: 12 }).map((_, i) => <Sk key={i} w={62} h={82} />)}
          </div>
          <Sk w={70} h={22} style={{ marginBottom: 4 }} />
        </div>
      </>
    );
  }
  return (
    <>
      {/* comp card */}
      <div style={{ padding: '14px 12px 0' }}>
        <div style={{ height: 'calc(100svh - 64px - 284px)', minHeight: 420, padding: 10, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', background: L.card, boxShadow: L.shadowLg, borderRadius: L.radiusLg }}>
          <Sk h="100%" r={0} style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 6px 4px' }}>
            {[0, 1, 2].map((i) => <div key={i}><Sk w={34} h={8} /><Sk w={54} h={15} style={{ marginTop: 6 }} /></div>)}
            <span style={{ flex: 1 }} />
            <Circle d={44} />
          </div>
        </div>
      </div>
      {/* up-next strip */}
      <div style={{ padding: '12px 12px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, flex: 1, overflow: 'hidden', padding: '4px' }}>
          {Array.from({ length: 7 }).map((_, i) => <Sk key={i} w={44} h={58} />)}
        </div>
        <Sk w={52} h={16} />
      </div>
    </>
  );
};

export const HomeSkeleton = () => {
  const isDesktop = useIsDesktop(DESKTOP);
  if (isDesktop) {
    return (
      <div aria-busy="true" aria-label="Loading">
        {/* masthead: dateline + headline | day strip | refine */}
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '34px 40px 18px', boxSizing: 'border-box', display: 'flex', alignItems: 'flex-end', gap: 32 }}>
          <div>
            <Sk w={300} h={12} />
            <Sk w={380} h={54} style={{ marginTop: 12 }} />
          </div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', paddingBottom: 6 }}><Sk w={360} h={44} /></div>
          <Sk w={150} h={44} style={{ marginBottom: 8 }} />
        </div>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 40px', boxSizing: 'border-box' }}>
          <Sk w={140} h={10} style={{ marginBottom: 12 }} />
          <Rule />
        </div>
        <HomeCardSkeleton />
      </div>
    );
  }
  return (
    <div aria-busy="true" aria-label="Loading">
      {/* edit line, headline + refine, day strip */}
      <div style={{ padding: '18px 16px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 9, borderBottom: `1px solid ${L.line}` }}>
          <Sk w={84} h={10} /><Sk w={92} h={10} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <Sk w="58%" h={34} /><Sk w={112} h={40} />
        </div>
        <Sk h={44} style={{ marginTop: 16 }} />
        <Rule style={{ marginTop: 12 }} />
      </div>
      <HomeCardSkeleton />
    </div>
  );
};

// ── Member salon (/client/me) ────────────────────────────────────────────
export const DashboardSkeleton = () => {
  const isDesktop = useIsDesktop(768);
  if (isDesktop) {
    return (
      <div aria-busy="true" aria-label="Loading" style={{ maxWidth: 1180, margin: '0 auto', padding: '40px 40px 60px', boxSizing: 'border-box' }}>
        <Rule style={{ background: L.ink, marginBottom: 32 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 56, alignItems: 'start' }}>
          <Sk h={450} />
          <div>
            <Sk w={180} h={11} />
            <Sk w="70%" h={76} style={{ marginTop: 18 }} />
            <Sk w="40%" h={14} style={{ marginTop: 16 }} />
            <div style={{ display: 'flex', marginTop: 34, borderTop: `1px solid ${L.line}`, borderBottom: `1px solid ${L.line}` }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ flex: 1, padding: '18px 0', borderLeft: i ? `1px solid ${L.line}` : 0, paddingLeft: i ? 24 : 0 }}>
                  <Sk w={60} h={34} /><Sk w={80} h={9} style={{ marginTop: 10 }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 28 }}><Sk w={150} h={46} /><Sk w={130} h={46} /></div>
            <Sk w={340} h={200} r={L.radiusLg} style={{ marginTop: 30 }} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 56, marginTop: 56 }}>
          <div>{Array.from({ length: 7 }).map((_, i) => <div key={i} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: `1px solid ${L.lineSoft}` }}><Sk w={22} h={14} /><Sk w="60%" h={14} /></div>)}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, borderTop: `1px solid ${L.line}` }}>
            {Array.from({ length: 6 }).map((_, i) => <div key={i} style={{ padding: 22, borderBottom: `1px solid ${L.line}`, borderRight: i % 3 !== 2 ? `1px solid ${L.line}` : 0 }}><Sk w={26} h={14} /><Sk w="70%" h={20} style={{ marginTop: 14 }} /><Sk w="50%" h={10} style={{ marginTop: 10 }} /></div>)}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div aria-busy="true" aria-label="Loading" style={{ paddingBottom: 100 }}>
      <Sk h="auto" r={0} style={{ aspectRatio: '4 / 5', width: '100%' }} />
      <div style={{ padding: '22px 20px 0' }}>
        <Sk h={190} r={L.radiusLg} />
        <div style={{ display: 'flex', marginTop: 22, borderTop: `1px solid ${L.line}`, borderBottom: `1px solid ${L.line}` }}>
          {[0, 1, 2].map((i) => <div key={i} style={{ flex: 1, padding: '16px 0', textAlign: 'center' }}><Sk w={40} h={26} style={{ margin: '0 auto' }} /><Sk w={56} h={8} style={{ margin: '10px auto 0' }} /></div>)}
        </div>
        <Sk w={90} h={10} style={{ marginTop: 28 }} />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0', borderBottom: `1px solid ${L.lineSoft}` }}>
            <Sk w={22} h={14} /><Sk w="55%" h={16} /><span style={{ flex: 1 }} /><Sk w={8} h={12} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Edit profile (/client/me/edit) ───────────────────────────────────────
const FieldSk = () => (
  <div style={{ paddingBottom: 18 }}>
    <Sk w={90} h={9} /><Sk h={1} r={0} style={{ marginTop: 26 }} />
  </div>
);
export const EditProfileSkeleton = () => {
  const isDesktop = useIsDesktop(768);
  if (isDesktop) {
    return (
      <div aria-busy="true" aria-label="Loading" style={{ maxWidth: 1180, margin: '0 auto', padding: '40px 40px 60px', boxSizing: 'border-box', display: 'grid', gridTemplateColumns: '280px 1fr', gap: 56 }}>
        <div>
          <Sk h={340} />
          <Sk w="70%" h={26} style={{ marginTop: 18 }} />
          <Sk w={140} h={40} style={{ marginTop: 16 }} />
          <div style={{ marginTop: 28 }}>{Array.from({ length: 6 }).map((_, i) => <div key={i} style={{ display: 'flex', gap: 12, padding: '11px 0' }}><Sk w={24} h={12} /><Sk w="60%" h={12} /></div>)}</div>
        </div>
        <div>
          <Sk w={120} h={10} />
          <Sk w="45%" h={44} style={{ marginTop: 14 }} />
          <Rule style={{ marginTop: 22, marginBottom: 30 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 40, rowGap: 14 }}>
            {Array.from({ length: 10 }).map((_, i) => <FieldSk key={i} />)}
          </div>
          <Sk w={170} h={46} style={{ marginTop: 24 }} />
        </div>
      </div>
    );
  }
  return (
    <div aria-busy="true" aria-label="Loading" style={{ paddingBottom: 110 }}>
      <div style={{ padding: '20px 20px 0' }}><Sk h="auto" style={{ aspectRatio: '4 / 5' }} /></div>
      <div style={{ display: 'flex', gap: 24, padding: '20px 16px 12px', borderBottom: `1px solid ${L.line}`, overflow: 'hidden' }}>
        {[0, 1, 2, 3].map((i) => <Sk key={i} w={70} h={11} />)}
      </div>
      <div style={{ padding: '22px 20px 0' }}>
        <Sk w="50%" h={28} style={{ marginBottom: 26 }} />
        {Array.from({ length: 6 }).map((_, i) => <FieldSk key={i} />)}
        <Sk h={48} style={{ marginTop: 14 }} />
      </div>
    </div>
  );
};

// ── Chat bubbles (inside /client/messages/:id) ───────────────────────────
export const ChatSkeleton = () => (
  <div aria-busy="true" aria-label="Loading" style={{ padding: '22px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 auto 6px' }}><Sk w={40} h={1} /><Sk w={70} h={9} /><Sk w={40} h={1} /></div>
    {[['l', '62%', 2], ['r', '48%', 1], ['l', '70%', 3], ['r', '56%', 2], ['l', '40%', 1]].map(([side, w, lines], i) => (
      <div key={i} style={{ display: 'flex', justifyContent: side === 'l' ? 'flex-start' : 'flex-end' }}>
        <div style={{ width: w, display: 'flex', flexDirection: 'column', gap: 6, alignItems: side === 'l' ? 'flex-start' : 'flex-end' }}>
          <Sk h={lines * 20 + 22} r={side === 'l' ? '2px 12px 12px 12px' : '12px 2px 12px 12px'} style={side === 'r' ? { opacity: 0.75 } : undefined} />
          <Sk w={44} h={8} />
        </div>
      </div>
    ))}
  </div>
);

// ── Generic bodies used only by the route skeleton ───────────────────────
const JournalBody = ({ isDesktop }) => (
  <div style={{ maxWidth: 1180, margin: '0 auto', padding: isDesktop ? '44px 40px' : '26px 20px', boxSizing: 'border-box' }}>
    <Sk w={220} h={10} />
    <Sk w={isDesktop ? 420 : '70%'} h={isDesktop ? 96 : 60} style={{ marginTop: 16 }} />
    <Sk w={isDesktop ? 300 : '55%'} h={16} style={{ marginTop: 14 }} />
    <Rule style={{ background: L.ink, marginTop: 26 }} />
    <div style={{ display: 'flex', gap: 22, padding: '14px 0', borderBottom: `1px solid ${L.line}` }}>{[0, 1, 2, 3].map((i) => <Sk key={i} w={60} h={10} />)}</div>
    <div style={{ display: isDesktop ? 'grid' : 'block', gridTemplateColumns: '1.2fr 1fr', gap: 44, marginTop: 30 }}>
      <Sk h="auto" style={{ aspectRatio: '4 / 5' }} />
      <div style={{ paddingTop: isDesktop ? 30 : 20 }}>
        <Sk w="34%" h={10} /><Sk w="92%" h={isDesktop ? 44 : 30} style={{ marginTop: 20 }} /><Sk w="70%" h={isDesktop ? 44 : 30} style={{ marginTop: 10 }} />
        <Sk w="96%" h={13} style={{ marginTop: 26 }} /><Sk w="88%" h={13} style={{ marginTop: 9 }} /><Sk w="60%" h={13} style={{ marginTop: 9 }} />
      </div>
    </div>
  </div>
);
const ArticleBody = ({ isDesktop }) => (
  <div style={{ maxWidth: 760, margin: '0 auto', padding: isDesktop ? '56px 24px' : '32px 20px', boxSizing: 'border-box' }}>
    <Sk w={140} h={10} style={{ margin: '0 auto 26px' }} />
    <Sk w="86%" h={isDesktop ? 52 : 36} style={{ margin: '0 auto 12px' }} />
    <Sk w="58%" h={isDesktop ? 52 : 36} style={{ margin: '0 auto 28px' }} />
    <Sk w={180} h={10} style={{ margin: '0 auto 40px' }} />
    <Sk h="auto" style={{ aspectRatio: '4 / 3' }} />
    {[96, 90, 94, 70].map((w, i) => <Sk key={i} w={`${w}%`} h={13} style={{ marginTop: i ? 10 : 34 }} />)}
  </div>
);
const InboxBody = ({ isDesktop }) => (
  <div style={{ maxWidth: isDesktop ? 1180 : 'none', margin: '0 auto', padding: isDesktop ? '40px 40px' : '26px 20px', boxSizing: 'border-box' }}>
    <Sk w={180} h={10} /><Sk w={isDesktop ? 320 : '60%'} h={isDesktop ? 60 : 44} style={{ marginTop: 14 }} />
    <div style={{ display: 'flex', gap: 22, margin: '24px 0 8px', borderBottom: `1px solid ${L.line}`, paddingBottom: 12 }}>{[0, 1, 2].map((i) => <Sk key={i} w={64} h={10} />)}</div>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 0', borderBottom: `1px solid ${L.lineSoft}`, maxWidth: isDesktop ? 420 : 'none' }}>
        <Circle d={52} />
        <div style={{ flex: 1 }}><Sk w={['46%', '38%', '52%', '30%', '44%', '40%'][i]} h={15} /><Sk w={['70%', '62%', '78%', '55%', '66%', '60%'][i]} h={10} style={{ marginTop: 10 }} /></div>
        <Sk w={34} h={8} />
      </div>
    ))}
  </div>
);
const ChatPage = () => (
  <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${L.line}` }}>
      <Sk w={18} h={18} /><Circle d={40} /><div><Sk w={110} h={16} /><Sk w={60} h={8} style={{ marginTop: 7 }} /></div>
    </div>
    <ChatSkeleton />
  </div>
);
const ConciergeBody = () => (
  <div style={{ maxWidth: 720, margin: '0 auto', padding: '56px 20px', boxSizing: 'border-box', textAlign: 'center' }}>
    <Sk w={150} h={10} style={{ margin: '0 auto' }} />
    <Sk w="64%" h={44} style={{ margin: '18px auto 0' }} />
    <Sk w={40} h={1} style={{ margin: '22px auto' }} />
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 30 }}>
      {[0, 1, 2, 3].map((i) => <Sk key={i} h={96} />)}
    </div>
    <Sk h={1} style={{ marginTop: 40 }} />
  </div>
);

// ── Portal chrome (masthead / tab bar) ───────────────────────────────────
const DesktopChrome = ({ children }) => (
  <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <div style={{ flexShrink: 0, borderBottom: `1px solid ${L.line}` }}>
      <div style={{ height: 76, maxWidth: 1600, margin: '0 auto', padding: '0 36px', display: 'flex', alignItems: 'center', gap: 24, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <DiamondMark size={30} strokeWidth={1.25} />
          <span style={{ fontFamily: L.serif, fontSize: 30, fontWeight: 500, letterSpacing: '.32em', lineHeight: 1, color: L.ink }}>FINDU</span>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 26 }}>
          {[44, 44, 52, 70, 76, 64, 64].map((w, i) => <Sk key={i} w={w} h={11} />)}
        </div>
        <Sk w={180} h={11} /><Circle d={34} /><Circle d={34} />
      </div>
    </div>
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</div>
  </div>
);
const MobileChrome = ({ children, hideTabs }) => (
  <div style={{ minHeight: '100vh', maxWidth: 600, margin: '0 auto', position: 'relative' }}>
    <div style={{ paddingBottom: hideTabs ? 0 : 84 }}>{children}</div>
    {!hideTabs && (
      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 600, height: 64, borderTop: `1px solid ${L.line}`, background: 'rgba(246,243,238,.92)', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Sk w={20} h={20} /><Sk w={34} h={7} /></div>)}
      </div>
    )}
  </div>
);

// Pick the page body for a /client path.
const bodyFor = (path, isDesktop) => {
  const p = (path || '').replace(/^\/preview(?=\/)/, '').replace(/\/+$/, '') || '/client';
  if (p === '/client') return <HomeSkeleton />;
  if (/^\/client\/trends\/(new|\d+\/edit)$/.test(p)) return <ArticleBody isDesktop={isDesktop} />;
  if (/^\/client\/trends\/\d+$/.test(p)) return <ArticleBody isDesktop={isDesktop} />;
  if (p.startsWith('/client/trends') || p === '/client/me/my-trends') return <JournalBody isDesktop={isDesktop} />;
  if (/^\/client\/messages\/[^/]+$/.test(p)) return <ChatPage />;
  if (p.startsWith('/client/messages')) return <InboxBody isDesktop={isDesktop} />;
  if (p === '/client/me/edit') return <EditProfileSkeleton />;
  if (p.startsWith('/client/me')) return <DashboardSkeleton />;
  if (p.startsWith('/client/ai')) return <ConciergeBody />;
  return <HomeSkeleton />;
};

export const ClientRouteSkeleton = ({ pathname }) => {
  const isDesktop = useIsDesktop(DESKTOP);
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/client');
  const p = path.replace(/^\/preview(?=\/)/, '');
  const hideTabs = /^\/client\/messages\/[^/]+$/.test(p) || /^\/client\/trends\/(\d+|new)/.test(p);
  const body = bodyFor(path, isDesktop);
  return (
    <div className="lx-root" style={{ background: L.paper, minHeight: '100vh' }}>
      <LuxeStyles />
      {isDesktop ? <DesktopChrome>{body}</DesktopChrome> : <MobileChrome hideTabs={hideTabs}>{body}</MobileChrome>}
    </div>
  );
};

export default ClientRouteSkeleton;
